import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';
import { AlertTriangle, Check } from 'lucide-react';

const TYPE_LABEL = { 'Strength Training': 'Strength', HIIT: 'HIIT', 'Fitness Racing': 'Fitness Racing' };
const fmt = (d) => format(parseISO(d), 'EEE, MMM d');

function Choice({ active, onClick, children, testId, disabled }) {
  return (
    <button onClick={onClick} data-testid={testId} disabled={disabled}
      className={`px-3 py-2 rounded-xl border text-sm text-left transition-colors ${active ? 'border-[#00C8FF] bg-[#00C8FF]/10 text-[#00C8FF]' : 'border-[var(--c-hair)] hover:bg-[var(--c-film)]'} disabled:cursor-not-allowed disabled:hover:bg-transparent`}>
      {children}
    </button>
  );
}

function Warning({ warn, onPick, onConfirm, saving, confirmLabel = 'Swap anyway' }) {
  return (
    <div className="space-y-3" data-testid="swap-warning">
      <div className="p-3 rounded-xl border border-[#FFB800]/40 bg-[#FFB800]/10 flex gap-2">
        <AlertTriangle className="w-4 h-4 text-[#FFB800] shrink-0 mt-0.5" />
        <div className="text-xs leading-relaxed">
          HIIT and Fitness Racing need at least two full rest days between them.
          {warn.warnings.map((w, i) => (
            <div key={i} className="mt-1 text-muted-foreground" data-testid={`swap-warning-${i}`}>
              {w.first} ({fmt(w.first_date)}) → {w.second} ({fmt(w.second_date)}): only {w.days_between} day{w.days_between === 1 ? '' : 's'} between.
            </div>
          ))}
        </div>
      </div>
      <div className="label-mini">Recommended alternatives</div>
      {warn.alternatives.map((alt, i) => (
        <button key={i} disabled={saving} onClick={() => onPick(alt)} data-testid={`swap-alt-${i}`}
          className="w-full p-2.5 rounded-xl border border-[var(--c-hair)] text-left text-sm hover:bg-[var(--c-film)] transition-colors">{alt.label}</button>
      ))}
      <Button variant="outline" disabled={saving} onClick={onConfirm} data-testid="swap-confirm-anyway" className="w-full rounded-xl border-[#FFB800]/50 text-[#FFB800]">
        {confirmLabel}
      </Button>
    </div>
  );
}

export function AddSessionDialog({ open, onOpenChange, freeDates, onDone, hasFr }) {
  const [type, setType] = useState('Strength Training');
  const [day, setDay] = useState(null);
  const [warn, setWarn] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) { setWarn(null); setDay(freeDates[0]); } }, [open]);
  const submit = async (body) => {
    setSaving(true);
    try {
      const r = await api.post('/workouts/extra-session', body);
      if (r.data.needs_confirm) { setWarn({ ...r.data, body, alternatives: r.data.alternatives.map(a => ({ ...a, new_type: a.type, new_date: a.date })) }); return; }
      toast.success('Extra session added');
      onOpenChange(false); onDone?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not add session'); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-regen-card border-[var(--c-hair)] max-w-sm" data-testid="add-session-dialog">
        <DialogHeader><DialogTitle>Add Extra Session</DialogTitle></DialogHeader>
        {warn ? <Warning warn={warn} saving={saving} onPick={(alt) => submit({ type: alt.type, date: alt.date })} onConfirm={() => submit({ ...warn.body, confirm: true })} confirmLabel="Add anyway" /> : (
          <div className="space-y-4">
            <p className="text-xs text-muted-foreground">Adds a session on top of your weekly programme.</p>
            <div className="grid grid-cols-3 gap-2">
              {Object.keys(TYPE_LABEL).filter(t => hasFr || t !== 'Fitness Racing').map(t => <Choice key={t} active={type === t} onClick={() => setType(t)} testId={`add-type-${t.replace(/\s+/g, '-').toLowerCase()}`}>{TYPE_LABEL[t]}</Choice>)}
            </div>
            <div className="grid grid-cols-2 gap-2">
              {freeDates.map(d => <Choice key={d} active={day === d} onClick={() => setDay(d)} testId={`add-day-${d}`}>{fmt(d)}</Choice>)}
            </div>
            <Button disabled={saving || !day} onClick={() => submit({ type, date: day })} data-testid="add-session-submit"
              className="w-full bg-[#00C8FF] hover:bg-[#00C8FF]/90 text-[#081726] font-semibold rounded-xl">{saving ? 'Adding…' : 'Add session'}</Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export function SessionSwapDialog({ assignment, open, onOpenChange, onDone }) {
  const [opts, setOpts] = useState(null);
  const [type, setType] = useState(null);
  const [day, setDay] = useState(null);
  const [warn, setWarn] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setOpts(null); setWarn(null);
    api.get(`/workouts/assignments/${assignment.id}/swap-options`).then(r => {
      setOpts(r.data); setType(r.data.current_type); setDay(r.data.current_date);
    }).catch(e => { toast.error(e.response?.data?.detail || 'Could not load swap options'); onOpenChange(false); });
  }, [open, assignment.id]);

  const submit = async (body) => {
    setSaving(true);
    try {
      const r = await api.post(`/workouts/assignments/${assignment.id}/swap`, body);
      if (r.data.needs_confirm) { setWarn({ ...r.data, body }); return; }
      toast.success(r.data.exchanged_with ? 'Sessions traded days' : 'Session updated');
      if (r.data.warnings?.length) toast.warning('Heads up: less than two rest days between HIIT and Fitness Racing.');
      onOpenChange(false); onDone?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not swap session'); }
    finally { setSaving(false); }
  };

  const unchanged = opts && type === opts.current_type && day === opts.current_date;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-regen-card border-[var(--c-hair)] max-w-sm" data-testid="session-swap-dialog">
        <DialogHeader><DialogTitle>Swap Session</DialogTitle></DialogHeader>
        {!opts && <div className="py-6 text-center text-sm text-muted-foreground">Loading…</div>}
        {opts && warn && <Warning warn={warn} saving={saving} onPick={(alt) => submit({ new_type: alt.new_type, new_date: alt.new_date })} onConfirm={() => submit({ ...warn.body, confirm: true })} />}
        {opts && !warn && (
          <div className="space-y-4">
            <p className="text-xs text-muted-foreground">Replaces this session. Your weekly total stays the same.</p>
            <div>
              <div className="label-mini mb-2">Session type</div>
              <div className="grid grid-cols-3 gap-2">
                {opts.types.map(t => <Choice key={t} active={type === t} onClick={() => setType(t)} testId={`swap-type-${t.replace(/\s+/g, '-').toLowerCase()}`}>{TYPE_LABEL[t] || t}</Choice>)}
              </div>
            </div>
            <div>
              <div className="label-mini mb-2">Day (Monday–Sunday)</div>
              <div className="grid grid-cols-2 gap-2">
                {(opts.days || []).map(d => {
                  const ok = ['current', 'free', 'swap'].includes(d.state);
                  return (
                    <Choice key={d.date} active={day === d.date} disabled={!ok} onClick={() => ok && setDay(d.date)} testId={`swap-day-${d.date}`}>
                      <span className={`flex flex-col items-start ${ok ? '' : 'opacity-40'}`}>
                        <span className="flex items-center gap-1">{fmt(d.date)}{d.state === 'current' && <Check className="w-3 h-3" />}</span>
                        {d.state === 'swap' && <span className="text-[10px] text-[#FFB800]">Trades days with {d.session || 'session'}</span>}
                        {d.state === 'past' && <span className="text-[10px]">Past</span>}
                        {d.state === 'before_start' && <span className="text-[10px]">Before programme start</span>}
                        {d.state === 'locked' && <span className="text-[10px]">Completed session</span>}
                      </span>
                    </Choice>
                  );
                })}
              </div>
            </div>
            <Button disabled={saving || unchanged} onClick={() => submit({ new_type: type, new_date: day })} data-testid="swap-submit"
              className="w-full bg-[#00C8FF] hover:bg-[#00C8FF]/90 text-[#081726] font-semibold rounded-xl">
              {saving ? 'Saving…' : 'Confirm swap'}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
